import { Ollama } from "ollama/browser";
import { GoogleGenerativeAI } from "@google/generative-ai";

/**
 * Abstract LLM inference that supports Ollama, Gemini Direct, and Gemini via Proxy
 * Controlled via environment variables:
 * - REACT_APP_INFERENCE_PROVIDER: "ollama", "gemini-direct", "gemini-proxy", or "gemini-proxy-local"
 * - REACT_APP_GEMINI_API_KEY: API key for Gemini (required when using gemini-direct)
 * - REACT_APP_GEMINI_PROXY_URL: Proxy server URL (required when using gemini-proxy)
 * - REACT_APP_GEMINI_MODEL: Model to use (used by both gemini-direct and gemini-proxy)
 * - REACT_APP_OLLAMA_MODEL: Model to use for Ollama (defaults to gemma3:1b)
 */

const INFERENCE_PROVIDER = process.env.REACT_APP_INFERENCE_PROVIDER;
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const GEMINI_PROXY_URL = process.env.REACT_APP_GEMINI_PROXY_URL;
const GEMINI_MODEL = process.env.REACT_APP_GEMINI_MODEL;
const OLLAMA_MODEL = process.env.REACT_APP_OLLAMA_MODEL;

/**
 * Stream text generation from the configured LLM provider
 * @param {string} systemPrompt - The system instruction for the model
 * @param {string} userPrompt - The user's input text
 * @param {function} onChunk - Callback function that receives each text chunk as it streams
 * @returns {Promise<string>} - The complete generated text
 */
export async function streamGeneration(systemPrompt, userPrompt, onChunk) {
  const providerInfo = getProviderInfo();
  console.log(`🤖 Using ${providerInfo.provider} (${providerInfo.model})`);

  if (INFERENCE_PROVIDER === "ollama") {
    return streamOllama(systemPrompt, userPrompt, onChunk);
  } else if (INFERENCE_PROVIDER === "gemini-direct") {
    return streamGeminiDirect(systemPrompt, userPrompt, onChunk);
  } else if (INFERENCE_PROVIDER === "gemini-proxy") {
    return streamGeminiProxy(systemPrompt, userPrompt, onChunk);
  } else if (INFERENCE_PROVIDER === "gemini-proxy-local") {
    return streamGeminiProxyLocal(systemPrompt, userPrompt, onChunk);
  } else {
    throw new Error(`Unknown inference provider: ${INFERENCE_PROVIDER}`);
  }
}

/**
 * Stream text generation using Ollama
 */
async function streamOllama(systemPrompt, userPrompt, onChunk) {
  const ollama = new Ollama({ host: "http://localhost:11434" });

  const stream = await ollama.chat({
    model: OLLAMA_MODEL,
    messages: [
      { role: "system", content: systemPrompt },
      { role: "user", content: userPrompt }
    ],
    stream: true,
  });

  let generatedText = "";

  for await (const chunk of stream) {
    const delta = chunk.message?.content ?? "";
    if (!delta) continue;
    generatedText += delta;
    onChunk(delta);
  }

  return generatedText;
}

/**
 * Stream text generation using Google Gemini API directly
 */
async function streamGeminiDirect(systemPrompt, userPrompt, onChunk) {
  if (!GEMINI_API_KEY) {
    throw new Error(
      "Gemini API key is required. Please set REACT_APP_GEMINI_API_KEY in your .env file."
    );
  }

  const genAI = new GoogleGenerativeAI(GEMINI_API_KEY);
  const model = genAI.getGenerativeModel({
    model: GEMINI_MODEL,
    systemInstruction: systemPrompt,
  });

  const result = await model.generateContentStream(userPrompt);

  let generatedText = "";

  for await (const chunk of result.stream) {
    const chunkText = chunk.text();
    generatedText += chunkText;
    onChunk(chunkText);
  }

  return generatedText;
}

/**
 * Shared helper function for streaming from a Gemini proxy server
 */
async function streamGeminiProxyHelper(proxyUrl, systemPrompt, userPrompt, onChunk) {
  const requestBody = {
    messages: [
      { content: systemPrompt },
      { content: userPrompt },
    ],
    // Note: Model selection is not supported by the proxy server
    // It will always default to gemini-2.0-flash-lite
  };

  const response = await fetch(proxyUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    throw new Error(`Proxy request failed: ${response.status} ${response.statusText}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Response body is not readable');
  }

  let generatedText = "";
  const decoder = new TextDecoder();

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value);
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('0:')) {
          // This is a text chunk from the AI SDK data stream format
          const text = line.substring(2);
          if (text) {
            generatedText += text;
            onChunk(text);
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  return generatedText;
}

/**
 * Stream text generation using Gemini API through proxy server
 */
async function streamGeminiProxy(systemPrompt, userPrompt, onChunk) {
  if (!GEMINI_PROXY_URL) {
    throw new Error(
      "Gemini proxy URL is required. Please set REACT_APP_GEMINI_PROXY_URL in your .env file."
    );
  }

  return streamGeminiProxyHelper(GEMINI_PROXY_URL, systemPrompt, userPrompt, onChunk);
}

/**
 * Stream text generation using local Gemini proxy server (localhost:3000)
 */
async function streamGeminiProxyLocal(systemPrompt, userPrompt, onChunk) {
  return streamGeminiProxyHelper('http://localhost:3000/api/gemini', systemPrompt, userPrompt, onChunk);
}

/**
 * Get information about the current LLM provider
 */
export function getProviderInfo() {
  let provider = "Unknown";
  let model = "";
  let isConfigured = false;

  if (INFERENCE_PROVIDER === "ollama") {
    provider = "Ollama";
    model = OLLAMA_MODEL;
    isConfigured = true;
  } else if (INFERENCE_PROVIDER === "gemini-direct") {
    provider = "Gemini (Direct)";
    model = GEMINI_MODEL;
    isConfigured = !!GEMINI_API_KEY;
  } else if (INFERENCE_PROVIDER === "gemini-proxy") {
    provider = "Gemini (Proxy)";
    model = GEMINI_MODEL;
    isConfigured = !!GEMINI_PROXY_URL;
  } else if (INFERENCE_PROVIDER === "gemini-proxy-local") {
    provider = "Gemini (Proxy Local)";
    model = GEMINI_MODEL;
    isConfigured = true; // Always configured since it uses localhost:3000
  }

  return {
    provider,
    model,
    isConfigured,
  };
}
